import type { CSSProperties } from 'react';
import {
  BREAKDOWN_PANEL_CATEGORIES,
  readableInkOn,
  type ElementCategory,
  type SceneItemView,
  type TextRange,
} from '@scoutaround/script-breakdown-shared';

import { linesWithOffsets } from '../scenes/sceneLabels.js';

/**
 * THE SCREENPLAY AS THE READER DRAWS IT, AND WHERE EACH ITEM IS MARKED IN IT.
 *
 * Pure, so the rules are asserted without a screen and computed once per scene (the reader
 * memoises on the detail it was given).
 *
 * MARKS GO WHERE THE SERVER SAYS, NEVER WHERE A NAME HAPPENS TO APPEAR. The old screen painted
 * the first `indexOf` of an item's name, so an ambiguous quote was silently presented as found in
 * one place. Each item now carries its evidence ranges (`ItemEvidence.ranges`, scene-relative),
 * and every one of them is marked — an AMBIGUOUS quote shows each occurrence and picks none.
 */

// ---- screenplay lines -----------------------------------------------------------

export type ScreenplayLineKind =
  | 'BLANK'
  | 'HEADING'
  | 'ACTION'
  | 'CHARACTER'
  | 'PARENTHETICAL'
  | 'DIALOGUE'
  | 'TRANSITION';

/** One line of the scene, with the characters it shows (leading and trailing spaces dropped). */
export interface ScreenplayLine {
  readonly index: number;
  readonly kind: ScreenplayLineKind;
  /** Offsets into the scene's text, so a mark lines up with the evidence ranges. */
  readonly start: number;
  readonly end: number;
}

const HEADING = /^(?:INT|EXT|EST|I\/E|INT\.?\/EXT|EXT\.?\/INT)[.\s/]/i;
const TRANSITION = /^(?:(?:FADE|CUT|DISSOLVE|SMASH CUT|MATCH CUT|WIPE)\b.*|.*\bTO:)$/;

/**
 * Screenplay layout from the text itself. A character cue is a short line in capitals; dialogue
 * is what follows it until a blank line. A rendering convenience, not a parse: guessing wrong
 * costs alignment, never meaning, and never moves a mark (offsets come from the text, not from
 * the layout).
 */
export function screenplayLines(text: string): ScreenplayLine[] {
  const lines: ScreenplayLine[] = [];
  let inDialogue = false;
  let previous: ScreenplayLineKind = 'BLANK';

  for (const [index, { offset, line }] of linesWithOffsets(text).entries()) {
    const trimmed = line.trim();
    const lead = line.length - line.trimStart().length;
    const start = offset + lead;
    const end = start + trimmed.length;

    if (trimmed === '') {
      inDialogue = false;
      previous = 'BLANK';
      lines.push({ index, kind: 'BLANK', start: offset, end: offset });
      continue;
    }

    const capitals = trimmed === trimmed.toUpperCase() && /\p{Lu}/u.test(trimmed);
    let kind: ScreenplayLineKind;

    if (!inDialogue && HEADING.test(trimmed)) kind = 'HEADING';
    else if (capitals && TRANSITION.test(trimmed)) kind = 'TRANSITION';
    // A shouted line straight under a cue is what they say, not a second speaker.
    else if (capitals && trimmed.length <= 40 && previous !== 'CHARACTER') kind = 'CHARACTER';
    else if (inDialogue && trimmed.startsWith('(')) kind = 'PARENTHETICAL';
    else if (inDialogue) kind = 'DIALOGUE';
    else kind = 'ACTION';

    if (kind === 'CHARACTER') inDialogue = true;
    previous = kind;
    lines.push({ index, kind, start, end });
  }

  return lines;
}

// ---- item marks --------------------------------------------------------------------

/**
 * How a mark is drawn, from what is known about it (rule 3):
 * - FILLED: on the sheet, at a verified quote;
 * - OUTLINED: awaiting a decision, at a verified quote;
 * - DASHED: found elsewhere in the scene, or one of several places it appears — to check;
 * - DOTTED: an approximate (name) match, never verified.
 */
export type MarkStyle = 'FILLED' | 'OUTLINED' | 'DASHED' | 'DOTTED';

export interface ItemMark {
  readonly key: string;
  readonly category: ElementCategory;
  readonly style: MarkStyle;
  readonly start: number;
  readonly end: number;
}

export function markStyleOf(item: Pick<SceneItemView, 'state' | 'evidence'>): MarkStyle {
  if (item.evidence.match === 'APPROXIMATE') return 'DOTTED';
  if (item.evidence.match === 'RELOCATED' || item.evidence.match === 'AMBIGUOUS') return 'DASHED';

  return item.state === 'ON_SHEET' ? 'FILLED' : 'OUTLINED';
}

/**
 * The colour half of a mark, in its category's colour (the shape half is `.sr-mark[data-style]`).
 * One function for the script and for the key under the chips, so the key draws what the script
 * draws. `colour` is a six-digit hex: the tints append an alpha pair to it.
 */
export function markPaint(style: MarkStyle, colour: string): CSSProperties {
  switch (style) {
    case 'FILLED':
      return { background: colour, color: readableInkOn(colour) };
    case 'OUTLINED':
      return { boxShadow: `inset 0 0 0 1.5px ${colour}`, background: `${colour}14` };
    case 'DASHED':
      return { outlineColor: colour, background: `${colour}0d` };
    case 'DOTTED':
      return { textDecorationColor: colour };
  }
}

function validRange(range: TextRange, length: number): TextRange | null {
  const start = Math.max(0, Math.min(range.start, length));
  const end = Math.max(0, Math.min(range.end, length));

  return end > start ? { start, end } : null;
}

/**
 * Every mark of a scene: in its text, and in its heading. An item marked not needed is out of the
 * text (it is off the sheet) unless it is the one selected; a category filter narrows the rest.
 */
export function itemMarks(
  items: readonly SceneItemView[],
  input: {
    readonly textLength: number;
    readonly headingLength: number;
    readonly category: ElementCategory | null;
    readonly selectedKey: string | null;
  },
): { text: ItemMark[]; heading: ItemMark[] } {
  const text: ItemMark[] = [];
  const heading: ItemMark[] = [];

  for (const item of items) {
    const selected = item.key === input.selectedKey;
    if (item.state === 'NOT_NEEDED' && !selected) continue;
    if (input.category !== null && item.category !== input.category && !selected) continue;

    const style = markStyleOf(item);
    const target = item.evidence.target === 'HEADING' ? heading : text;
    const length = item.evidence.target === 'HEADING' ? input.headingLength : input.textLength;

    for (const range of item.evidence.ranges) {
      const valid = validRange(range, length);
      if (valid !== null) target.push({ key: item.key, category: item.category, style, ...valid });
    }
  }

  return { text, heading };
}

// ---- runs: one primary highlight per character range --------------------------------

/** A stretch of text covered by the same items; the first key is the one drawn. */
export interface MarkRun {
  readonly start: number;
  readonly end: number;
  readonly keys: readonly string[];
  /** The mark drawn for the run: the first key's. */
  readonly primary: ItemMark;
}

const STYLE_RANK: Readonly<Record<MarkStyle, number>> = { FILLED: 0, OUTLINED: 1, DASHED: 2, DOTTED: 3 };
const CATEGORY_RANK = new Map(BREAKDOWN_PANEL_CATEGORIES.map((c, i) => [c, i]));

/**
 * Cut the text into runs where the set of covering marks is constant. Where items overlap, ONE is
 * drawn — the selected one, else the firmest (on the sheet before awaiting before to-check before
 * approximate), else the narrowest — and the others are listed on the mark's popover.
 */
export function markRuns(marks: readonly ItemMark[], selectedKey: string | null): MarkRun[] {
  if (marks.length === 0) return [];

  const rank = (m: ItemMark): [number, number, number, number] => [
    m.key === selectedKey ? 0 : 1,
    STYLE_RANK[m.style],
    m.end - m.start,
    CATEGORY_RANK.get(m.category) ?? 99,
  ];
  const before = (a: ItemMark, b: ItemMark): number => {
    const ra = rank(a);
    const rb = rank(b);
    for (let i = 0; i < ra.length; i += 1) {
      const d = (ra[i] ?? 0) - (rb[i] ?? 0);
      if (d !== 0) return d;
    }

    return a.key.localeCompare(b.key);
  };

  const points = [...new Set(marks.flatMap((m) => [m.start, m.end]))].sort((a, b) => a - b);
  const runs: MarkRun[] = [];

  for (let i = 0; i < points.length - 1; i += 1) {
    const start = points[i] as number;
    const end = points[i + 1] as number;
    const covering = marks.filter((m) => m.start <= start && m.end >= end).sort(before);
    if (covering.length === 0) continue;

    const keys = [...new Set(covering.map((m) => m.key))];
    const primary = covering[0] as ItemMark;
    const last = runs[runs.length - 1];

    // One run for adjacent stretches covered by exactly the same items.
    if (last !== undefined && last.end === start && last.keys.join('|') === keys.join('|')) {
      runs[runs.length - 1] = { ...last, end };
    } else {
      runs.push({ start, end, keys, primary });
    }
  }

  return runs;
}

/** A line's characters, cut where runs begin and end; `run` is null for unmarked text. */
export function lineSegments(
  line: { readonly start: number; readonly end: number },
  runs: readonly MarkRun[],
): { start: number; end: number; run: MarkRun | null }[] {
  const segments: { start: number; end: number; run: MarkRun | null }[] = [];
  let at = line.start;

  for (const run of runs) {
    if (run.end <= line.start || run.start >= line.end) continue;
    const from = Math.max(run.start, line.start);
    const to = Math.min(run.end, line.end);
    if (from > at) segments.push({ start: at, end: from, run: null });
    segments.push({ start: from, end: to, run });
    at = to;
  }
  if (at < line.end) segments.push({ start: at, end: line.end, run: null });

  return segments;
}
